import { Component } from '@theme/component';
import { fetchConfig, debounce, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { CartStore } from '@theme/cart-store';
import {
  ThemeEvents,
  CartUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 120).bind(this);
  #cartShareDocumentClick = this.#handleCartShareDocumentClick.bind(this);
  #cartShareDocumentKeydown = this.#handleCartShareDocumentKeydown.bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener('click', this.#cartShareDocumentClick);
    document.addEventListener('keydown', this.#cartShareDocumentKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('click', this.#cartShareDocumentClick);
    document.removeEventListener('keydown', this.#cartShareDocumentKeydown);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  #getSelectedShareItems() {
    const cards = this.querySelectorAll('.cart__card-item');
    const items = [];
    for (const card of cards) {
      const cb = card.querySelector('.cart__card-checkbox');
      if (!cb?.checked) continue;
      const variantId = card.dataset.variantId;
      const input = card.querySelector('input[name="updates[]"]');
      const qty = input ? parseInt(input.value, 10) || 0 : 0;
      if (variantId && qty > 0) items.push({ id: parseInt(variantId, 10), quantity: qty });
    }
    return items;
  }

  #buildShareUrl() {
    const items = this.#getSelectedShareItems();
    if (items.length === 0) return '';
    const encoded = items.map((i) => `${i.id}:${i.quantity}`).join(',');
    const cartPath = Theme.routes?.cart_url?.replace(window.location.origin, '') || '/cart';
    const base = `${window.location.origin}${cartPath}`;
    return `${base}${cartPath.includes('?') ? '&' : '?'}cart_share=${encodeURIComponent(encoded)}`;
  }

  #closeCartSharePopovers() {
    this.querySelectorAll('[data-cart-share-popover]').forEach((popover) => {
      popover.hidden = true;
    });
    this.querySelectorAll('[data-cart-share]').forEach((shareBtn) => {
      shareBtn.setAttribute('aria-expanded', 'false');
    });
  }

  #resetCartShareCopyLabel(copyBtn) {
    if (!copyBtn) return;
    copyBtn.disabled = false;
    const labelEl = copyBtn.querySelector('.product-details__share-popover-label');
    const defaultLabel = copyBtn.dataset.defaultLabel || copyBtn.getAttribute('data-default-label') || 'Copy link';
    if (labelEl) {
      labelEl.textContent = defaultLabel;
    } else {
      copyBtn.textContent = defaultLabel;
    }
  }

  #updateCartSharePopoverLinks(popover, url) {
    const title = popover.getAttribute('data-share-title') || document.title;
    const titleEncoded = encodeURIComponent(title);
    const urlEncoded = encodeURIComponent(url);
    popover.dataset.shareUrl = url;

    const whatsapp = popover.querySelector('[data-cart-share-channel="whatsapp"]');
    if (whatsapp) whatsapp.href = `https://wa.me/?text=${titleEncoded}%20${urlEncoded}`;

    const facebook = popover.querySelector('[data-cart-share-channel="facebook"]');
    if (facebook) facebook.href = `https://www.facebook.com/sharer/sharer.php?u=${urlEncoded}`;

    const telegram = popover.querySelector('[data-cart-share-channel="telegram"]');
    if (telegram) telegram.href = `https://t.me/share/url?url=${urlEncoded}&text=${titleEncoded}`;
  }

  #openCartSharePopover(shareBtn) {
    const block = shareBtn.closest('.cart__share-block');
    const popover = block ? block.querySelector('[data-cart-share-popover]') : null;
    const url = this.#buildShareUrl();
    if (!popover || !url) return;

    this.#updateCartSharePopoverLinks(popover, url);
    popover.querySelectorAll('[data-cart-share-copy]').forEach((copyBtn) => {
      this.#resetCartShareCopyLabel(copyBtn);
    });
    popover.hidden = false;
    shareBtn.setAttribute('aria-expanded', 'true');
  }

  #copyCartShareUrl(url, copyBtn) {
    if (!url || !copyBtn) return;

    const copiedLabel = copyBtn.getAttribute('data-copied-label') || 'Copied!';
    const labelEl = copyBtn.querySelector('.product-details__share-popover-label');
    const defaultLabel = copyBtn.getAttribute('data-default-label') || copyBtn.dataset.defaultLabel || (labelEl ? labelEl.textContent : copyBtn.textContent);

    const onCopied = () => {
      if (labelEl) {
        labelEl.textContent = copiedLabel;
      } else {
        copyBtn.textContent = copiedLabel;
      }
      copyBtn.disabled = true;
      window.setTimeout(() => {
        copyBtn.disabled = false;
        this.#closeCartSharePopovers();
      }, 800);
    };

    if (!copyBtn.dataset.defaultLabel) {
      copyBtn.dataset.defaultLabel = defaultLabel;
    }

    const copyWithFallback = () => {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      textarea.setSelectionRange(0, textarea.value.length);

      try {
        if (document.execCommand('copy')) onCopied();
      } catch (error) {
        console.warn('Failed to copy cart share URL with execCommand', error);
      } finally {
        document.body.removeChild(textarea);
      }
    };

    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(onCopied).catch((error) => {
        console.warn('Failed to copy cart share URL to clipboard', error);
        copyWithFallback();
      });
      return;
    }

    copyWithFallback();
  }

  #handleCartShareDocumentClick(event) {
    const copyBtn = event.target.closest('[data-cart-share-copy]');
    if (copyBtn && this.contains(copyBtn)) {
      event.preventDefault();
      event.stopPropagation();
      const popover = copyBtn.closest('[data-cart-share-popover]');
      const url = popover ? popover.getAttribute('data-share-url') || popover.dataset.shareUrl || '' : '';
      if (url) this.#copyCartShareUrl(url, copyBtn);
      return;
    }

    const shareChannel = event.target.closest('[data-cart-share-popover] a[data-cart-share-channel]');
    if (shareChannel && this.contains(shareChannel)) {
      this.#closeCartSharePopovers();
      return;
    }

    if (!event.target.closest('[data-cart-share-popover]') && !event.target.closest('[data-cart-share]')) {
      this.#closeCartSharePopovers();
    }
  }

  #handleCartShareDocumentKeydown(event) {
    if (event.key === 'Escape') this.#closeCartSharePopovers();
  }

  onShareCart() {
    const shareBtn = this.querySelector('[data-cart-share]');
    const block = shareBtn ? shareBtn.closest('.cart__share-block') : null;
    const popover = block ? block.querySelector('[data-cart-share-popover]') : null;
    if (!shareBtn || !popover) return;
    if (!this.#buildShareUrl()) return;

    const willOpen = popover.hidden;
    this.#closeCartSharePopovers();
    if (willOpen) this.#openCartSharePopover(shareBtn);
  }

  onDeleteSelected() {
    const cards = this.querySelectorAll('.cart__card-item');
    const updates = {};
    const cardsToRemove = [];
    for (const card of cards) {
      const key = card.dataset.itemKey;
      const cb = card.querySelector('.cart__card-checkbox');
      const input = card.querySelector('input[name="updates[]"]');
      const qty = input ? parseInt(input.value, 10) || 0 : 0;
      if (key) updates[key] = cb && cb.checked ? 0 : qty;
      if (cb?.checked && key) cardsToRemove.push(card);
    }
    const hasRemovals = Object.values(updates).some((q) => q === 0);
    if (!hasRemovals) return;

    const doRemove = () => this.#removeLinesAndReload(updates);
    if (prefersReducedMotion() || cardsToRemove.length === 0) return doRemove();

    cardsToRemove.forEach((card) => {
      this.#markCardRemoving(card);
    });

    const REMOVE_ANIMATION_MS = 700;
    setTimeout(doRemove, REMOVE_ANIMATION_MS);
  }

  onCheckoutWithSelected() {
    const cards = this.querySelectorAll('.cart__card-item');
    const updates = {};
    let hasUnselected = false;
    for (const card of cards) {
      const key = card.dataset.itemKey;
      const cb = card.querySelector('.cart__card-checkbox');
      const input = card.querySelector('input[name="updates[]"]');
      const qty = input ? parseInt(input.value, 10) || 0 : 0;
      const selected = cb && cb.checked;
      if (!selected && qty > 0) hasUnselected = true;
      if (key) updates[key] = selected ? qty : 0;
    }
    if (Object.values(updates).every((q) => q === 0)) return;
    if (!hasUnselected) {
      this.#awaitVisitSync().then(() => {
        window.location.href = '/checkout';
      });
      return;
    }
    this.#removeLinesAndRedirect(updates);
  }

  #awaitVisitSync() {
    if (!window.ThemeVisitContext || typeof window.ThemeVisitContext.syncToCart !== 'function') {
      return Promise.resolve();
    }
    return window.ThemeVisitContext.syncToCart().catch(() => false);
  }

  #markCardRemoving(card) {
    if (!card) return;
    card.classList.add('removing');
    const nextDivider = card.nextElementSibling;
    if (nextDivider?.classList?.contains('cart__items-divider')) {
      nextDivider.classList.add('removing');
    }
  }

  #stripRemovingCards() {
    this.querySelectorAll('.cart__card-item.removing, .cart__items-divider.removing').forEach((node) => {
      node.remove();
    });
  }

  #removeLinesAndReload(updates) {
    this.#disableCartItems();
    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ updates, sections: this.sectionId }) }))
      .then((r) => r.json())
      .then((data) => {
        if (data.errors) return;
        this.#stripRemovingCards();
        if (data.sections?.[this.sectionId]) morphSection(this.sectionId, data.sections[this.sectionId]);
      })
      .catch(console.error)
      .finally(() => this.#enableCartItems());
  }

  #removeLinesAndRedirect(updates) {
    this.#disableCartItems();
    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify({ updates }) }))
      .then((r) => r.json())
      .then(() => this.#awaitVisitSync())
      .then(() => { window.location.href = '/checkout'; })
      .catch(console.error)
      .finally(() => this.#enableCartItems());
  }

  onLineItemRemove(line) {
    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];
    if (!cartItemRowToRemove) return;

    const doRemove = () => {
      this.updateQuantity({
        line,
        id: cartItemRowToRemove.dataset.itemKey || '',
        quantity: 0,
        action: 'clear',
      });
    };

    if (prefersReducedMotion()) return doRemove();

    this.#markCardRemoving(cartItemRowToRemove);

    const REMOVE_ANIMATION_MS = 700;
    setTimeout(doRemove, REMOVE_ANIMATION_MS);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity, id } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const payload = {
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    };
    if (id) {
      payload.id = id;
    } else {
      payload.line = line;
    }
    const body = JSON.stringify(payload);

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.querySelectorAll('.cart__card-item.removing, .cart__items-divider.removing').forEach((node) => {
            node.classList.remove('removing');
          });
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        this.#stripRemovingCards();

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        if (Array.isArray(parsedResponseText.items)) {
          CartStore.set(parsedResponseText);
          document.dispatchEvent(new CustomEvent('cart:updated', {
            bubbles: true,
            detail: {
              cart: parsedResponseText,
              item_count: parsedResponseText.item_count,
              items: parsedResponseText.items,
              sections: parsedResponseText.sections,
            },
          }));
        } else {
          fetch('/cart.js', fetchConfig('json'))
            .then(function(response) { return response.json(); })
            .then(function(cart) {
              CartStore.set(cart);
              document.dispatchEvent(new CustomEvent('cart:updated', {
                bubbles: true,
                detail: {
                  cart: cart,
                  item_count: cart.item_count,
                  items: cart.items,
                  sections: parsedResponseText.sections,
                },
              }));
            })
            .catch(function() {});
        }

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    if (event.detail?.resource?.sections?.[this.sectionId]) return;
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
      if (cartItemsHtml) {
        await morphSection(this.sectionId, cartItemsHtml);
      } else {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      }
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      await morphSection(this.sectionId, cartItemsHtml);
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
